import type {
    AudioAsset,
    AudioSourceData,
    LuminarySettings,
    MediaSourceData,
    PlaybackRegion,
    SceneGroup,
    TemplateBundle,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';
import { $getSelection, type LexicalEditor } from 'lexical';

import type { AudioAssetModel } from 'js/src/client';
import { ApiService, TranscriptAssetType } from 'js/src/client';
import {
    CLIP_CREATOR_LUMINARY_RESOLUTION,
    CLIP_CREATOR_WAVEFORM_SCENE_DESIGN,
    ClipCreatorQueryParams,
} from 'js/src/data/clip-creator/clip-creator-consts';
import type { ClipData } from 'js/src/data/clip-creator/slices/clip-creator-clips-slice';
import type { ClipCreatorPage } from 'js/src/data/clip-creator/slices/clip-creator-pages-slice';
import { ALL_VIDEOS_VIEW_ID } from 'js/src/data/dashboard';
import { regenerateTranscriptForAsset } from 'js/src/data/media';
import {
    getSceneDesign,
    getTemplateBundle,
    getTemplateSceneIds,
    getVoiceoverSceneDesignsForTemplate,
} from 'js/src/data/templates/templates-selectors';
import {
    $getSelectedNodes,
    $getWordNodesBetweenTimes,
    convertTranscriptWordsToMillis,
} from 'js/src/data/transcript';
import {
    $isTranscriptWordNode,
    type TranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { LocalStorageKeys, localStorageRemove, localStorageSet } from 'js/src/libs/local-storage';
import Navigation from 'js/src/libs/navigation';
import { getQueryParam } from 'js/src/libs/querystring';
import type { RootState } from 'js/src/libs/RootState';
import { DEFAULT_LUMINARY_CONFIG } from 'js/src/libs/video-data-consts';
import {
    getVoiceDrivenAsset,
    isVoiceDrivenSceneGroup,
    withDataLuminarySync,
} from 'js/src/luminary';

const createEmptyLuminaryInstance = (settings: LuminarySettings = {}) => {
    const luminary = new Luminary({
        ...DEFAULT_LUMINARY_CONFIG,
        ...settings,
    });
    return luminary;
};

export const createEmptyClipLuminaryInstance = (name: string = 'clip') => {
    const luminary = createEmptyLuminaryInstance({
        name,
        resolutionValue: 300,
        fetchResources: true,
    });
    return luminary;
};

/**
 * @deprecated use createEmptyClipLuminaryInstance instead
 */
export const createEmptyBlueprintPreviewLuminaryInstance = (name: string = 'blueprintPreview') => {
    const luminary = createEmptyLuminaryInstance({
        name,
        resolutionValue: CLIP_CREATOR_LUMINARY_RESOLUTION,
    });
    return luminary;
};

export const getSceneGroupByIdOrThrow = (luminary: Luminary, sceneGroupId: string) => {
    const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
    if (!sceneGroup) {
        throw new Error(`Scene group with id ${sceneGroupId} not found`);
    }
    return sceneGroup;
};

export const getSceneGroupByIdOrUndefined = (luminary: Luminary, sceneGroupId: string) => {
    try {
        return getSceneGroupByIdOrThrow(luminary, sceneGroupId);
    } catch (e) {
        return undefined;
    }
};

/**
 * Clip creator voicedriven scene group has to meet the following conditions:
 * - should be a voice driven scene group
 * - should be the first scene group in luminary
 * - should be the only scene group in luminary
 * - should be used in clip creator context
 */
export const getVoiceDrivenSceneGroupOrThrowFromLuminary = (
    luminary: Luminary | Readonly<Luminary>,
) => {
    const nonBrandingSceneGroups: SceneGroup[] = [];
    luminary.scenes.forEach((scene) => {
        if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
            return;
        }
        const sceneGroup = scene.sceneGroup!;
        if (nonBrandingSceneGroups.includes(sceneGroup)) {
            return;
        }
        nonBrandingSceneGroups.push(sceneGroup);
    });
    if (nonBrandingSceneGroups.length === 0) {
        throw new Error('Luminary has zero scene groups');
    }
    if (nonBrandingSceneGroups.length > 1) {
        throw new Error('Luminary has more than 1 scene group');
    }
    const sceneGroup = nonBrandingSceneGroups[0];
    if (!isVoiceDrivenSceneGroup(sceneGroup)) {
        throw new Error('Scene group is not a voice driven scene group');
    }
    return sceneGroup;
};

export const getVoiceDrivenSceneGroupIdOrThrowFromLuminary = (luminary: Luminary) => {
    const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
    return sceneGroup.id;
};

export const getVoiceDrivenAssetOrThrowFromLuminary = (
    luminary: Luminary,
): TimeAwareMediaAsset | AudioAsset => {
    const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
    const asset = getVoiceDrivenAsset(sceneGroup);
    if (!asset) {
        throw new Error('Voice driven asset not found');
    }
    return asset;
};

export const getVoiceDrivenAssetSourceDataOrThrowFromLuminary = (
    luminary: Luminary,
): MediaSourceData | AudioSourceData => {
    const asset = getVoiceDrivenAssetOrThrowFromLuminary(luminary);
    const sourceData = asset.sourceData;
    if (!sourceData) {
        throw new Error('Voice driven asset source data not found');
    }
    return sourceData;
};

export const getVoiceDrivenSceneGroupOrUndefinedFromLuminary = (luminary: Luminary) => {
    try {
        return getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
    } catch (e) {
        return undefined;
    }
};

export const getVoiceDrivenSceneGroupIdOrUndefinedFromLuminary = (luminary: Luminary) => {
    try {
        return getVoiceDrivenSceneGroupIdOrThrowFromLuminary(luminary);
    } catch (e) {
        return undefined;
    }
};

export const getVoiceDrivenAssetOrUndefinedFromLuminary = (luminary: Luminary) => {
    try {
        return getVoiceDrivenAssetOrThrowFromLuminary(luminary);
    } catch (e) {
        return undefined;
    }
};

export const getVoiceDrivenAssetSourceDataOrUndefinedFromLuminary = (luminary: Luminary) => {
    try {
        return getVoiceDrivenAssetSourceDataOrThrowFromLuminary(luminary);
    } catch (e) {
        return undefined;
    }
};

export const setLuminaryTemplate = (luminary: Luminary, templateBundle: TemplateBundle) => {
    luminary.setTemplateBundle(templateBundle);
};

export const isEmptyClip = (clip: ClipData) => {
    return clip.hasCuts === false;
};

export const isSavedClip = (clip: ClipData) => {
    return clip.videoId !== undefined;
};

export const getClipCreatorProjectId = () => {
    // The clip creator can be entered with a `?project_id=…` query param to scope
    // the resulting clip to a specific project. Without that, the clip lands at
    // the workspace root.
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('project_id');
    if (!raw) return undefined;
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed === ALL_VIDEOS_VIEW_ID) return undefined;
    return parsed;
};

export const assertSourceDataHasAudio = (asset: MediaSourceData) => {
    if (!asset.has_audio) {
        throw new Error('Asset does not have audio');
    }
};

export const assertSourceDataHasVoiceover = async (asset: MediaSourceData) => {
    const videoId = asset.uuid;
    let hasVoiceover = asset.has_voiceover;
    if (hasVoiceover === null) {
        hasVoiceover = await regenerateTranscriptForAsset(
            videoId,
            TranscriptAssetType.VIDEO,
            undefined,
        );
    }
    if (!hasVoiceover) {
        throw new Error('Asset does not have voiceover');
    }
};

export const retrieveTranscriptForAsset = async (assetId: string, isAudioOnly: boolean) => {
    const response = await ApiService.assetTranscriptRetrieve({
        path: { asset_id: assetId },
        query: { asset_type: isAudioOnly ? TranscriptAssetType.AUDIO : TranscriptAssetType.VIDEO },
    });
    const transcriptWords = convertTranscriptWordsToMillis(response.data.transcriptWords);
    const transcriptLanguage = response.data.language;
    return {
        transcriptWords,
        transcriptLanguage,
    };
};

export const assertAudioSourceDataHasVoiceover = async (asset: AudioSourceData) => {
    const audioId = asset.uuid;
    if (!audioId) {
        throw new Error('Audio asset does not have uuid');
    }
    const hasVoiceover = await regenerateTranscriptForAsset(
        audioId,
        TranscriptAssetType.AUDIO,
        undefined,
    );
    if (!hasVoiceover) {
        throw new Error('Audio asset does not have voiceover');
    }
};

export const getScriptFromTranscriptWords = (
    editor: LexicalEditor,
    playbackRegions: PlaybackRegion[],
) => {
    return editor.getEditorState().read(() => {
        const wordNodes: TranscriptWordNode[] = [];
        for (const [startTime, endTime] of playbackRegions) {
            wordNodes.push(...$getWordNodesBetweenTimes(startTime, endTime, true));
        }
        const script = wordNodes.map((node) => node.getTextContent()).join('');
        return script;
    });
};

export const shouldEnableAllPages = () => {
    // If the query string has enableAllPages set to 1, we enable all pages.
    return getQueryParam(ClipCreatorQueryParams.enableAllPages) === '1';
};

export const trackClipCreatorLastPage = (page: ClipCreatorPage, feedback = true) => {
    localStorageRemove(LocalStorageKeys.CLIP_CREATOR_ENTRY_TIME);
    localStorageSet(LocalStorageKeys.CLIP_CREATOR_LAST_PAGE, {
        lastPage: page,
        timestamp: Date.now(),
        feedback,
    });
};

export const isTranscriptTextHighlighted = (editor: LexicalEditor): boolean => {
    // Check if user has selected a part of the transcript
    return editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!selection || selection.isCollapsed()) {
            return false;
        }

        const selectedNodes = $getSelectedNodes();
        const selectedWordNodes = selectedNodes.filter($isTranscriptWordNode);
        return selectedWordNodes.length > 0;
    });
};

/**
 * Updates the current URL's query parameters for the clip creator without triggering navigation.
 * Used to preserve state across page refreshes (e.g., keeping uploaded media ID).
 *
 * @param params - Object containing key-value pairs to set/delete in query params
 */
export const updateClipCreatorQueryParams = (
    params: Partial<Record<ClipCreatorQueryParams, string | number | boolean>>,
) => {
    const url = new URL(window.location.href);
    const keys = Object.keys(params);
    keys.forEach((key) => {
        const value = params[key as ClipCreatorQueryParams];
        if (value === undefined || value === null) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, String(value));
        }
    });
    const newUrl = url.toString();
    Navigation.replaceState(newUrl);
};

export const buildAudioSourceData = (audioAsset: AudioAssetModel): AudioSourceData => {
    return {
        uuid: audioAsset.uuid,
        mp3_url: audioAsset.mp3_url,
        duration: audioAsset.duration,
        source: audioAsset.source ?? 0,
        source_id: audioAsset.source_id ?? null,
        license: audioAsset.license ?? 0,
        attribution: audioAsset.attribution ?? null,
        description: audioAsset.description ?? null,
        tags: audioAsset.tags ?? [],
        title: audioAsset.title ?? null,
        name: audioAsset.name ?? '',
        quality: audioAsset.quality ?? 0,
        ai_voice: audioAsset.ai_voice ?? null,
        ai_voice_speed: audioAsset.ai_voice_speed ?? null,
    };
};

/**
 * Chooses which scene design to use for creating a waveform overlay.
 *
 * Case 1: Use the default CLIP_CREATOR_WAVEFORM_SCENE_DESIGN if it exists in the template.
 * Case 2: Find a voiceover scene design that contains a waveform icon asset.
 * Case 3: Fall back to a fancy caption scene design and add waveform manually.
 */
export const chooseWaveformSceneDesign = (state: RootState, templateId: string) => {
    const sceneDesignIds = getTemplateSceneIds(state, templateId);

    // Case 1
    if (sceneDesignIds.includes(CLIP_CREATOR_WAVEFORM_SCENE_DESIGN)) {
        const sceneDesign = getSceneDesign(state, CLIP_CREATOR_WAVEFORM_SCENE_DESIGN);
        if (sceneDesign) {
            return CLIP_CREATOR_WAVEFORM_SCENE_DESIGN;
        }
    }

    // Case 2
    const voiceoverSceneDesigns = getVoiceoverSceneDesignsForTemplate(state, templateId);

    const templateBundle = getTemplateBundle(state, templateId);
    if (!templateBundle) {
        return null;
    }
    const waveformSceneDesignId = withDataLuminarySync(
        (dataLuminary) => {
            for (const { id: sceneDesignId } of voiceoverSceneDesigns) {
                const scene = dataLuminary.addScene({ sceneDesignId });
                const hasWaveform = scene
                    .getAssetsByType(VideoData.ICON_ASSET_TYPE)
                    .some((iconAsset) => iconAsset.playbackMode === VideoData.PLAYBACK_WAVEFORM);
                if (hasWaveform) {
                    return sceneDesignId;
                }
            }
            return null;
        },
        { templateBundle: templateBundle || undefined },
    );

    if (waveformSceneDesignId) {
        return waveformSceneDesignId;
    }

    // Case 3
    const fancyCaptionDesign = voiceoverSceneDesigns.find((design) => {
        const designName = design.id.split(':').pop() ?? '';
        return designName.startsWith('fancy_');
    });
    if (fancyCaptionDesign) {
        return fancyCaptionDesign.id;
    }

    return null;
};

type Bounds = { x: number; y: number; width: number; height: number };

/**
 * Finds a position for a new asset that avoids overlapping existing assets.
 */
export const findNonOverlappingPosition = (
    existingBounds: Bounds[],
    canvasWidth: number,
    canvasHeight: number,
    assetWidth: number,
    assetHeight: number,
): { x: number; y: number } => {
    const columns = [0.5, 0.25, 0.75];
    const rows = [0.85, 0.75, 0.5, 0.25, 0.15];

    for (const rowFrac of rows) {
        for (const colFrac of columns) {
            const cx = canvasWidth * colFrac;
            const cy = canvasHeight * rowFrac;
            const left = cx - assetWidth / 2;
            const right = cx + assetWidth / 2;
            const top = cy - assetHeight / 2;
            const bottom = cy + assetHeight / 2;
            const overlaps = existingBounds.some((b) => {
                const dx = Math.min(right, b.x + b.width) - Math.max(left, b.x);
                const dy = Math.min(bottom, b.y + b.height) - Math.max(top, b.y);
                return dx > 0 && dy > 0;
            });
            if (!overlaps) {
                return { x: cx, y: cy };
            }
        }
    }
    return { x: canvasWidth * 0.5, y: canvasHeight * 0.85 };
};
